import { isEmpty } from "lodash";
import { ROUTES } from "src/routes";

import { AddressInput, SubmitRegistrationInput } from "@api/graphql";
import { FormStep } from "@components/form/MultiStepForm";

import { ParticipantType } from "./ParticipantType";
import {
  ParticipantTypeInput,
  RegisterSubmissionDataIntersection,
  UserDetailsInput,
  UserInput,
  UserRoleInput,
} from "./RegisterFormTypes";
import { UserDetails as UserDetailsComponent } from "./UserDetails";
import { UserRole } from "./UserRole";

export const steps = (
  path: typeof ROUTES.Dashboard.NewAccounts | typeof ROUTES.RegisterSubmission,
  isCreatedUserCompletingRegistration: boolean,
): (
  | FormStep<UserInput, RegisterSubmissionDataIntersection>
  | FormStep<UserInput, UserDetailsInput, { isCreatedUserCompletingRegistration: boolean }>
)[] => [
  {
    route: path.$.UserRole.relativePath,
    displayName: "I want to",
    component: UserRole,
    transitionSubmit: () => path.$.ParticipantType.relativePath,
    shouldMoveToNextStep: isCreatedUserCompletingRegistration,
    mergeData: (multiFormData, formData) => ({
      ...multiFormData,
      user_role: formData.user_role,
    }),
  } as FormStep<UserInput, UserRoleInput>,
  {
    route: path.$.ParticipantType.relativePath,
    displayName: "I’m looking to",
    component: ParticipantType,
    transitionCancel: () => path.$.UserRole.relativePath,
    transitionSubmit: () => path.$.UserDetails.relativePath,
    shouldMoveToNextStep: isCreatedUserCompletingRegistration,
    mergeData: (multiFormData, formData) => ({
      ...multiFormData,
      participant_type: formData.participant_type,
    }),
  } as FormStep<UserInput, ParticipantTypeInput>,
  {
    route: path.$.UserDetails.relativePath,
    displayName: "Your details",
    component: UserDetailsComponent,
    transitionCancel: () => path.$.ParticipantType.relativePath,
    mergeKey: "user_details",
    additionalComponentProps: { isCreatedUserCompletingRegistration: isCreatedUserCompletingRegistration },
  } as FormStep<UserInput, UserDetailsInput, { isCreatedUserCompletingRegistration: boolean }>,
];

export const mapRegisterFromInput = (input: UserInput): SubmitRegistrationInput => {
  const { user_details, user_role, participant_type } = input;
  const { account_details, bank_details, finance_contact } = user_details;

  const isAddressValid = (address: AddressInput | undefined) => {
    return address && Object.keys(address).length > 0;
  };

  const accountDetails = account_details && isAddressValid(account_details.address) ? account_details : undefined;

  const financeContact = !isEmpty(finance_contact) ? finance_contact : undefined;

  const bankDetails = bank_details?.bank_type ? bank_details : undefined;
  const isJoinExistingParticipant = user_details.join_existing_participant || false;
  const contactDetails = !isEmpty(user_details.contact_details)
    ? {
        ...user_details.contact_details,
        ...(user_details.contact_details?.middle_name ? { middle_name: user_details.contact_details.middle_name } : {}),
      }
    : undefined;
  return {
    terms_accepted: !isJoinExistingParticipant ? user_details.terms_accepted : undefined,
    receive_newsletter: user_details.receive_newsletter || false,
    join_existing_participant: isJoinExistingParticipant,
    user_role: user_role,
    participant_type: participant_type,
    user_details: contactDetails,
    account_details: accountDetails,
    tax_details: user_details.tax_details,
    finance_contact: financeContact,
    bank_details: bankDetails,
  };
};
